<template>
  <div class="flex flex-col pb-2">

    <!-- Custom date range form -->
    <template v-if="newComponentStore.newComponent.type === 'custom'">
      <div class="apex-datepicker flex flex-col gap-3">
        <!-- Range summary chip (tap to toggle calendar) -->
        <div class="cdf-range-chip" :class="{ 'cdf-range-chip--active': isCompleteRange(mainRange) }"
          @click="mainCalendarOpen = !mainCalendarOpen">
          <CalendarIcon class="w-4 h-4 flex-shrink-0" />
          <span v-if="isCompleteRange(mainRange)" class="cdf-range-text">{{ formatRangeFromStrings(mainRange) }}</span>
          <span v-else class="cdf-range-text cdf-range-placeholder">{{ t('newcomponent.dates.custom.period_placeholder') }}</span>
          <button v-if="isCompleteRange(mainRange)" type="button" class="cdf-range-clear"
            @click.stop="mainRange = null; mainCalendarOpen = true">
            <XMarkIcon class="w-3.5 h-3.5" />
          </button>
          <svg class="cdf-chevron" :class="{ 'cdf-chevron--open': mainCalendarOpen }" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
          </svg>
        </div>

        <!-- Collapsible calendar -->
        <Transition name="calendar-reveal">
          <div v-if="mainCalendarOpen" class="cdf-calendar-wrap">
            <VueDatePicker
              v-model="mainRange"
              range
              inline
              :enable-time-picker="false"
              auto-apply
              model-type="yyyy-MM-dd"
              :max-date="new Date()"
              :dark="isDark"
            />
          </div>
        </Transition>

        <!-- Toggle comparison -->
        <InputToggle
          name="compare"
          :model-value="showComparison"
          @update:model-value="showComparison = $event"
          :label="t('newcomponent.dates.custom.compare')"
        />

        <!-- Comparison period (collapsible) -->
        <Transition name="slide-down">
          <div v-if="showComparison" class="flex flex-col gap-2 pl-3 cdf-compare-border">
            <div class="cdf-range-chip cdf-range-chip--sm" :class="{ 'cdf-range-chip--active': isCompleteRange(compareRange) }"
              @click="compareCalendarOpen = !compareCalendarOpen">
              <CalendarIcon class="w-3.5 h-3.5 flex-shrink-0" />
              <span v-if="isCompleteRange(compareRange)" class="cdf-range-text">{{ formatRangeFromStrings(compareRange) }}</span>
              <span v-else class="cdf-range-text cdf-range-placeholder">{{ t('newcomponent.dates.custom.compare_placeholder') }}</span>
              <button v-if="isCompleteRange(compareRange)" type="button" class="cdf-range-clear"
                @click.stop="compareRange = null; compareCalendarOpen = true">
                <XMarkIcon class="w-3.5 h-3.5" />
              </button>
              <svg class="cdf-chevron" :class="{ 'cdf-chevron--open': compareCalendarOpen }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clip-rule="evenodd" />
              </svg>
            </div>
            <Transition name="calendar-reveal">
              <div v-if="compareCalendarOpen" class="cdf-calendar-wrap">
                <VueDatePicker
                  v-model="compareRange"
                  range
                  inline
                  :enable-time-picker="false"
                  auto-apply
                  model-type="yyyy-MM-dd"
                  :max-date="new Date()"
                  :dark="isDark"
                />
              </div>
            </Transition>
          </div>
        </Transition>
      </div>
    </template>

    <!-- Standard period form (existing behavior) -->
    <template v-else>
      <div class="flex">
        <InputGroup v-model="newComponentStore.newComponent.periodBeforeDate"
          :unit="labels[newComponentStore.newComponent.type]" width="lg"
          name="periodBeforeDate" col @update:model-value="updatePreviousDate"
          type="number" />

        <Popper arrow :placement="'bottom'">
          <QuestionMarkCircleIcon class="w-5 h-5 text-muted-foreground ml-2" />
          <template #content>
            <div class="bg-slate-900 text-white rounded-lg p-3 text-sm">
              <p class="py-1">
                {{
                  $t(`editcomponent.date.helper.${newComponentStore.newComponent.type}.today`,
                    todayHelperDate) }}
              </p>
              <p class="py-1">
                {{
                  $t(`editcomponent.date.helper.${newComponentStore.newComponent.type}.span`,
                    { span: newComponentStore.newComponent.periodBeforeDate })
                }}
              </p>

              <p class="py-1">
                {{
                  $t(`editcomponent.date.helper.${newComponentStore.newComponent.type}.compared`,
                    {
                      start: todayHelperDate.today,
                      compared:
                        dateUtils.formatYear(newComponentStore.newComponent.previousDate!.date.toJSDate())
                    })
                }}
              </p>
            </div>
          </template>
        </Popper>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';
import { QuestionMarkCircleIcon, CalendarIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import InputGroup from '../../components/form/input/InputGroup.vue';
import InputToggle from '../../components/form/input/InputToggle.vue';
import Popper from 'vue3-popper';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { useI18n } from 'vue-i18n';
import { dateUtils } from '../../utils/DateUtils';
import { useNewComponentStore } from '../../stores/newComponentStore';
import { SalesReportSettingsType } from '../../SalesReport';
import { DateTime } from 'luxon';
const { t } = useI18n();
const newComponentStore = useNewComponentStore();
const chartType = newComponentStore.newComponent.type

const isDark = computed(() => document.documentElement.classList.contains('dark'));

const mainCalendarOpen = ref(true);
const compareCalendarOpen = ref(true);

const formatRangeFromStrings = (range: string[]) => {
  if (!range || range.length < 2) return '';
  const start = DateTime.fromISO(range[0]);
  const end = DateTime.fromISO(range[1]);
  return `${start.toFormat('dd/MM/yyyy')}  →  ${end.toFormat('dd/MM/yyyy')}`;
};

// Main range as [start, end] array for VueDatePicker range mode
const mainRange = ref<string[] | null>(
  newComponentStore.newComponent.customStartDate && newComponentStore.newComponent.customEndDate
    ? [newComponentStore.newComponent.customStartDate, newComponentStore.newComponent.customEndDate]
    : null
);

const showComparison = ref(
  !!(newComponentStore.newComponent.previousCustomStartDate || newComponentStore.newComponent.previousCustomEndDate)
);

// Comparison range as [start, end] array
const compareRange = ref<string[] | null>(
  newComponentStore.newComponent.previousCustomStartDate && newComponentStore.newComponent.previousCustomEndDate
    ? [newComponentStore.newComponent.previousCustomStartDate, newComponentStore.newComponent.previousCustomEndDate]
    : null
);

const isCompleteRange = (val: string[] | null): val is [string, string] =>
  !!val && val.length === 2 && !!val[0] && !!val[1];

// Sync main range to the store + auto-collapse
watch(mainRange, (val) => {
  if (isCompleteRange(val)) {
    newComponentStore.newComponent.customStartDate = val[0];
    newComponentStore.newComponent.customEndDate = val[1];
    mainCalendarOpen.value = false;
  } else {
    newComponentStore.newComponent.customStartDate = undefined;
    newComponentStore.newComponent.customEndDate = undefined;
  }
});

// Sync comparison range to the store + auto-collapse
watch(compareRange, (val) => {
  if (isCompleteRange(val) && showComparison.value) {
    newComponentStore.newComponent.previousCustomStartDate = val[0];
    newComponentStore.newComponent.previousCustomEndDate = val[1];
    compareCalendarOpen.value = false;
  } else {
    newComponentStore.newComponent.previousCustomStartDate = undefined;
    newComponentStore.newComponent.previousCustomEndDate = undefined;
  }
});

watch(showComparison, (val) => {
  if (!val) {
    newComponentStore.newComponent.previousCustomStartDate = undefined;
    newComponentStore.newComponent.previousCustomEndDate = undefined;
    compareRange.value = null;
  }
});

// Dynamically compute interval based on custom range duration
watch(mainRange, (val) => {
  if (val && val.length === 2) {
    const diffDays = DateTime.fromISO(val[1]).diff(DateTime.fromISO(val[0]), 'days').days;
    if (diffDays < 90) {
      newComponentStore.newComponent.interval = 'day';
    } else {
      newComponentStore.newComponent.interval = 'month';
    }
  }
});

const todayHelperDate = computed(() => {
  let formattedDate = {
    today: '',
    end: ''
  }

  switch (newComponentStore.newComponent.type) {
    case 'daily':
      formattedDate = {
        today: dateUtils.formatDay(DateTime.now().toJSDate()),
        end: dateUtils.formatDay(DateTime.now().toJSDate())
      }
      break;
    case 'weekly':
      const today = dateUtils.formatDay(DateTime.now().startOf('week', {
        useLocaleWeeks: false
      }).toJSDate())

      const end = dateUtils.formatDay(DateTime.now().endOf('week', {
        useLocaleWeeks: false
      }).toJSDate())
      formattedDate = { today, end }
      break;
    case 'monthly':
      formattedDate = { today: dateUtils.formatMonth(new Date()), end: '' }
      break;
    case 'yearly':
      formattedDate = { today: dateUtils.formatYear(new Date()), end: '' }
      break;
    default:
      formattedDate = { today: dateUtils.formatDay(new Date()), end: '' }
      break;
  }

  return formattedDate
})

const labels = computed(() => {
  return {
    daily: t('newcomponent.dates.day', newComponentStore.newComponent.periodBeforeDate),
    weekly: t('newcomponent.dates.week', newComponentStore.newComponent.periodBeforeDate),
    monthly: t('newcomponent.dates.month', newComponentStore.newComponent.periodBeforeDate),
    yearly: t('newcomponent.dates.year', newComponentStore.newComponent.periodBeforeDate),
    custom: ''
  }
})

const defaultSpans: Record<SalesReportSettingsType, number> = {
  daily: 7,
  weekly: 1,
  monthly: 1,
  yearly: 1,
  custom: 0
}

const previousDate = ref('')

const updatePreviousDate = () => {
  previousDate.value = dateUtils.formatDay(newComponentStore.newComponent.previousDate!.date.toJSDate())
}

onMounted(() => {
  if (newComponentStore.newComponent.periodBeforeDate === 0 && chartType !== 'custom') {
    newComponentStore.newComponent.periodBeforeDate = defaultSpans[chartType]
  }
})
</script>

<style>
/* ═══════════════════════════════════════════════════════
   Apex Timing — VueDatePicker Theme
   Racing-grade datepicker: cyan accent, Lato typography,
   rounded surfaces, snappy micro-interactions
   ═══════════════════════════════════════════════════════ */

/* ── Hide time picker toggle (we use enable-time-picker=false) ── */
.apex-datepicker .dp__time_display,
.apex-datepicker .dp__action_extra,
.apex-datepicker .dp--tp-wrap,
.apex-datepicker .dp__btn.dp--time-btn,
.apex-datepicker .dp__btn[data-test-id="time-toggle-overlay-btn"],
.apex-datepicker .dp__time_col,
.apex-datepicker .dp__overlay_container--tp,
.apex-dp-menu .dp__time_display,
.apex-dp-menu .dp__action_extra,
.apex-dp-menu .dp--tp-wrap,
.apex-dp-menu .dp__btn.dp--time-btn,
.apex-dp-menu .dp__btn[data-test-id="time-toggle-overlay-btn"],
.apex-dp-menu .dp__time_col,
.apex-dp-menu .dp__overlay_container--tp {
  display: none !important;
}

/* ── Custom slot input ── */
.apex-dp-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  height: 46px;
  border-radius: 0.75rem;
  background: var(--card);
  border: 1px solid var(--border);
  transition: border-color 200ms ease, box-shadow 200ms ease;
  cursor: pointer;
}

.apex-dp-input-wrap:hover {
  border-color: rgba(6, 200, 255, 0.35);
}

.apex-dp-input-wrap.apex-dp-input-focus {
  border-color: #06C8FF;
  box-shadow: 0 0 0 3px rgba(6, 200, 255, 0.15);
}

.apex-dp-icon {
  width: 20px;
  height: 20px;
  color: #06C8FF;
  margin-left: 12px;
  flex-shrink: 0;
}

.apex-dp-custom-input {
  flex: 1;
  height: 100%;
  padding: 0 12px;
  background: transparent;
  border: none;
  outline: none;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.01em;
  color: var(--foreground);
  cursor: pointer;
}

.apex-dp-custom-input::placeholder {
  color: var(--muted-foreground);
  font-weight: 400;
}

.apex-dp-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-right: 6px;
  border-radius: 0.375rem;
  color: var(--muted-foreground);
  transition: color 150ms ease;
  cursor: pointer;
  background: none;
  border: none;
}

.apex-dp-clear:hover {
  color: #06C8FF;
}

/* ── Dark mode — custom input ── */
:is(.dark *) .apex-dp-input-wrap {
  border-color: rgba(255, 255, 255, 0.1);
}

:is(.dark *) .apex-dp-input-wrap:hover {
  border-color: rgba(6, 200, 255, 0.3);
}

:is(.dark *) .apex-dp-input-wrap.apex-dp-input-focus {
  border-color: #06C8FF;
  box-shadow: 0 0 0 3px rgba(6, 200, 255, 0.1);
}

/* ── Original dp__input (fallback) ── */
.apex-datepicker .dp__input_wrap .dp__input {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.01em;
  height: 46px;
  border-radius: 0.75rem;
  background: var(--card);
  color: var(--foreground);
  border: 1px solid var(--border);
  padding-left: 2.75rem;
  padding-right: 1rem;
  transition: border-color 200ms ease, box-shadow 200ms ease;
}

.apex-datepicker .dp__input_wrap .dp__input::placeholder {
  color: var(--muted-foreground);
  font-weight: 400;
}

.apex-datepicker .dp__input_wrap .dp__input:hover {
  border-color: rgba(6, 200, 255, 0.35);
}

.apex-datepicker .dp__input_wrap .dp__input:focus,
.apex-datepicker .dp__input_wrap .dp__input.dp__input_focus {
  border-color: #06C8FF;
  box-shadow: 0 0 0 3px rgba(6, 200, 255, 0.15);
  outline: none;
}

/* ── Input icon — cyan accent ── */
.apex-datepicker .dp__input_icon {
  color: #06C8FF;
  left: 12px;
  width: 20px;
  height: 20px;
}

/* Hide native clear buttons — we use a custom one via dp-input slot */
.apex-datepicker .dp__clear_icon,
.apex-datepicker .dp--clear-btn {
  display: none !important;
}

/* ── Menu / dropdown panel (targets both inline and teleported menus) ── */
:is(.apex-datepicker, .apex-dp-menu).dp__menu,
:is(.apex-datepicker) .dp__menu {
  font-family: 'Lato', sans-serif;
  border-radius: 1rem;
  border: 1px solid var(--border);
  background: var(--card);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.07),
    0 12px 28px -6px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  padding: 6px;
}

/* ── Range summary chip ── */
.cdf-range-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  background: var(--muted);
  color: var(--muted-foreground);
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  transition: all 200ms ease;
}

.cdf-range-chip--active {
  background: rgba(6, 200, 255, 0.08);
  color: #06C8FF;
  border: 1px solid rgba(6, 200, 255, 0.2);
}

.cdf-range-chip--sm {
  padding: 6px 10px;
  font-size: 12px;
}

.cdf-range-text {
  flex: 1;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.cdf-range-placeholder {
  font-weight: 400;
  opacity: 0.7;
}

.cdf-range-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 150ms ease;
}

.cdf-range-clear:hover {
  opacity: 1;
}

:is(.dark) .cdf-range-chip--active {
  background: rgba(6, 200, 255, 0.06);
  border-color: rgba(6, 200, 255, 0.15);
}

/* ── Chevron ── */
.cdf-chevron {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  opacity: 0.5;
  transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1);
}

.cdf-chevron--open {
  transform: rotate(180deg);
}

/* ── Calendar wrapper ── */
.cdf-calendar-wrap {
  overflow: hidden;
  border-radius: 1rem;
}

/* ── Month/year header ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__month_year_wrap {
  gap: 4px;
}

:is(.apex-datepicker, .apex-dp-menu) .dp__month_year_select {
  font-family: 'Lato', sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: var(--foreground);
  border-radius: 0.5rem;
  height: 36px;
  transition: background 150ms ease, color 150ms ease;
}

:is(.apex-datepicker, .apex-dp-menu) .dp__month_year_select:hover {
  background: rgba(6, 200, 255, 0.08);
  color: #06C8FF;
}

/* ── Navigation arrows ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__inner_nav {
  width: 32px;
  height: 32px;
  border-radius: 0.5rem;
  color: var(--muted-foreground);
  transition: all 150ms ease;
}

:is(.apex-datepicker, .apex-dp-menu) .dp__inner_nav:hover {
  background: rgba(6, 200, 255, 0.1);
  color: #06C8FF;
  transform: scale(1.05);
}

:is(.apex-datepicker, .apex-dp-menu) .dp__inner_nav svg {
  width: 18px;
  height: 18px;
}

/* ── Weekday headers ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__calendar_header_item {
  font-family: 'Lato', sans-serif;
  font-weight: 800;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--muted-foreground);
  height: 32px;
}

:is(.apex-datepicker, .apex-dp-menu) .dp__calendar_header_separator {
  display: none;
}

/* ── Calendar day cells ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__cell_inner {
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  font-weight: 500;
  width: 36px;
  height: 36px;
  border-radius: 0.5rem;
  transition: all 120ms ease;
  color: var(--foreground);
}

:is(.apex-datepicker, .apex-dp-menu) .dp__cell_inner:hover:not(.dp__cell_disabled) {
  background: rgba(6, 200, 255, 0.1);
  color: #06C8FF;
  transform: scale(1.08);
}

/* ── Today marker — subtle cyan ring ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__today {
  border: 1.5px solid #06C8FF;
}

/* ── Active / selected endpoints — solid cyan pill ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__active_date,
:is(.apex-datepicker, .apex-dp-menu) .dp__range_start,
:is(.apex-datepicker, .apex-dp-menu) .dp__range_end {
  background: #06C8FF !important;
  color: #1B1C1E !important;
  font-weight: 700;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(6, 200, 255, 0.35);
}

/* ── Range start: left-rounded ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__range_start {
  border-radius: 0.5rem 0 0 0.5rem;
}

/* ── Range end: right-rounded ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__range_end {
  border-radius: 0 0.5rem 0.5rem 0;
}

/* ── Range between days — cyan wash ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__range_between {
  background: rgba(6, 200, 255, 0.1);
  color: var(--foreground);
  border-radius: 0;
  border-top: 1.5px solid rgba(6, 200, 255, 0.2);
  border-bottom: 1.5px solid rgba(6, 200, 255, 0.2);
}

/* ── Disabled / off-range days ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__cell_disabled,
:is(.apex-datepicker, .apex-dp-menu) .dp__cell_offset {
  color: var(--muted-foreground);
  opacity: 0.3;
}

/* ── Overlay (month/year grid selection) ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__overlay {
  background: var(--card);
  border-radius: 1rem;
}

:is(.apex-datepicker, .apex-dp-menu) .dp__overlay_cell {
  font-family: 'Lato', sans-serif;
  font-weight: 500;
  border-radius: 0.5rem;
  color: var(--foreground);
  transition: all 150ms ease;
}

:is(.apex-datepicker, .apex-dp-menu) .dp__overlay_cell:hover {
  background: rgba(6, 200, 255, 0.1);
  color: #06C8FF;
}

:is(.apex-datepicker, .apex-dp-menu) .dp__overlay_cell_active {
  background: #06C8FF !important;
  color: #1B1C1E !important;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(6, 200, 255, 0.3);
}

/* ── Action row buttons ── */
:is(.apex-datepicker, .apex-dp-menu) .dp__action_button {
  font-family: 'Lato', sans-serif;
  font-weight: 600;
  border-radius: 0.5rem;
  font-size: 13px;
  transition: all 150ms ease;
}

:is(.apex-datepicker, .apex-dp-menu) .dp__action_buttons .dp__action_select {
  background: #06C8FF;
  color: #1B1C1E;
}

:is(.apex-datepicker, .apex-dp-menu) .dp__action_buttons .dp__action_select:hover {
  background: #35AAD5;
}

:is(.apex-datepicker, .apex-dp-menu) .dp__action_buttons .dp__action_cancel {
  color: var(--muted-foreground);
  border-color: var(--border);
}

/* ═══════════════════════════════════════════════════════
   Dark mode — deeper surfaces, richer glows
   ═══════════════════════════════════════════════════════ */
:is(.dark *) .apex-datepicker .dp__input_wrap .dp__input {
  background: var(--card);
  color: var(--foreground);
  border-color: rgba(255, 255, 255, 0.1);
}

:is(.dark *) .apex-datepicker .dp__input_wrap .dp__input:hover {
  border-color: rgba(6, 200, 255, 0.3);
}

:is(.dark *) .apex-datepicker .dp__input_wrap .dp__input:focus,
:is(.dark *) .apex-datepicker .dp__input_wrap .dp__input.dp__input_focus {
  border-color: #06C8FF;
  box-shadow: 0 0 0 3px rgba(6, 200, 255, 0.1);
}

:is(.dark) :is(.apex-datepicker, .apex-dp-menu).dp__menu,
:is(.dark) :is(.apex-datepicker) .dp__menu,
.dp__theme_dark.apex-dp-menu {
  background: #282727;
  border-color: rgba(255, 255, 255, 0.06);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.4),
    0 12px 28px -6px rgba(0, 0, 0, 0.5);
}

:is(.dark) :is(.apex-datepicker, .apex-dp-menu) .dp__cell_inner:hover:not(.dp__cell_disabled) {
  background: rgba(6, 200, 255, 0.15);
}

:is(.dark) :is(.apex-datepicker, .apex-dp-menu) .dp__range_between {
  background: rgba(6, 200, 255, 0.08);
  border-color: rgba(6, 200, 255, 0.15);
}

:is(.dark) :is(.apex-datepicker, .apex-dp-menu) .dp__active_date,
:is(.dark) :is(.apex-datepicker, .apex-dp-menu) .dp__range_start,
:is(.dark) :is(.apex-datepicker, .apex-dp-menu) .dp__range_end {
  box-shadow: 0 2px 12px rgba(6, 200, 255, 0.4);
}

:is(.dark) :is(.apex-datepicker, .apex-dp-menu) .dp__overlay {
  background: #282727;
}

:is(.dark) :is(.apex-datepicker, .apex-dp-menu) .dp__inner_nav:hover {
  background: rgba(6, 200, 255, 0.15);
}

:is(.dark) :is(.apex-datepicker, .apex-dp-menu) .dp__month_year_select:hover {
  background: rgba(6, 200, 255, 0.12);
}

:is(.dark) :is(.apex-datepicker, .apex-dp-menu) .dp__overlay_cell_active {
  box-shadow: 0 2px 12px rgba(6, 200, 255, 0.35);
}
</style>

<style scoped>
/* ── Comparison border ── */
.cdf-compare-border {
  border-left: 2px solid rgba(6, 200, 255, 0.30);
}
:is(.dark *).cdf-compare-border {
  border-left: 2px solid rgba(6, 200, 255, 0.20);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
  transform: translateY(-10px);
}

.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  max-height: 500px;
  transform: translateY(0);
}

/* ── Calendar reveal ── */
.calendar-reveal-enter-active {
  transition: max-height 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease;
}
.calendar-reveal-leave-active {
  transition: max-height 250ms cubic-bezier(0.4, 0, 0.2, 1), opacity 150ms ease;
}
.calendar-reveal-enter-from,
.calendar-reveal-leave-to {
  max-height: 0;
  opacity: 0;
}
.calendar-reveal-enter-to,
.calendar-reveal-leave-from {
  max-height: 400px;
  opacity: 1;
}

</style>
